import { debounce } from "lodash";
import { getCompanyStaffIds, getRecentInviter } from "@/services/share";
import { IStaffItem } from "@/types/share";
import { useState, useEffect, useRef } from "react";

let page = 0; // 页码
const DefaultPageSize = 20;

const useGetStaffList = (shareId: string) => {
  const [staffList, setStaffList] = useState<IStaffItem[]>([]);
  const [subscribedList, setSubscribedList] = useState<IStaffItem[]>([]);
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [showLoadEnd, setShowLoadEnd] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const latestReqTime = useRef(Date.now());
  const keywordRef = useRef("");

  const isLoadMore = useRef(false); // 是否正在加载更多
  const subscribedListRef = useRef<IStaffItem[]>([]);
  subscribedListRef.current = subscribedList;

  const loadList = (now: number, handle?: () => void) => {
    if (page === 0) {
      setStaffList([]);
      setShowLoadEnd(false);
    }
    page++;
    loadListFn(now, handle);
  };

  const loadListFn = async (now: number, handle?: () => void) => {
    try {
      const res = await getCompanyStaffIds({
        shareId,
        keyword: keywordRef.current,
        page,
        size: DefaultPageSize,
      });
      if (now !== latestReqTime.current) return;

      const formatList = res.data.data.map((item) => {
        return {
          ...item,
          subscribed:
            item.subscribed ||
            subscribedListRef.current.find(
              (subItem) => subItem.identityId === item.identityId
            ),
          disabled: item.subscribed,
        };
      }) as IStaffItem[];
      setStaffList(page === 1 ? formatList : staffList.concat(formatList));
      if (res.data.pageIndex >= res.data.totalPages) {
        setShowLoadEnd(true); 
        console.log("当前页数==总页数");
      }
      handle?.();
    } catch (error) {
      setIsLoading(false);
      setLoadError(true);
    }
  };

  const onListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!keywordRef.current) return;
    const scrollTop = e.currentTarget.scrollTop; // 距顶部
    const clientHeight = e.currentTarget.clientHeight; // 可视区高度
    const scrollHeight = e.currentTarget.scrollHeight; // 滚动条总高度
    if (!isLoadMore.current && scrollTop + clientHeight + 40 >= scrollHeight) {
      if (showLoadEnd) {
        console.log("已经加载完毕，无需再次加载");
      } else {
        isLoadMore.current = true;
        setShowLoadMore(true);
        const now = Date.now();
        latestReqTime.current = now;
        loadList(now, () => {
          isLoadMore.current = false;
          setShowLoadMore(false);
        });
      }
    }
  };

  const fetchRecentInviter = async (now: number) => {
    try {
      const res = await getRecentInviter(shareId);
      if (now !== latestReqTime.current) return;
      const formatList = res.data.data.map((item) => {
        return {
          ...item,
          realname: item.name,
          subscribed:
            item.subscribed ||
            subscribedListRef.current.find(
              (subItem) => subItem.identityId === item.identityId
            ),
          disabled: item.subscribed,
        };
      }) as IStaffItem[];
      setStaffList([...formatList]);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setLoadError(true);
    }
  };

  const fetchStaffList = async (reservePage?: boolean) => {
    const now = Date.now();
    latestReqTime.current = now;
    setLoadError(false);
    setIsLoading(true);
    if (keywordRef.current) {
      if (!reservePage) {
        page = 0;
      }
      loadList(now, () => {
        setIsLoading(false);
      });
    } else {
      fetchRecentInviter(now);
    }
  };

  const retry = () => {
    fetchStaffList(true);
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  const debounceFn = useRef<Function>(
    debounce(() => {
      fetchStaffList();
    }, 300)
  );

  useEffect(() => {
    if (!shareId) return;
    keywordRef.current = keyword;
    debounceFn.current();
  }, [shareId, keyword]);

  return {
    keyword,
    staffList,
    setKeyword,
    setStaffList,
    subscribedList,
    setSubscribedList,
    onListScroll,
    showLoadMore,
    isLoading,
    loadError,
    retry,
  };
};

export default useGetStaffList;
